<template>
  <div
    v-if="showActions"
    class="order-customer-actions"
    :class="layout === 'compact' ? 'order-customer-actions--compact' : 'panel-card p-3'"
  >
    <template v-if="layout === 'compact'">
      <div class="actions-inline">
        <button
          v-if="canPay"
          type="button"
          class="btn btn-primary btn-sm actions-btn-compact"
          :disabled="paying"
          :title="'Pagar pedido #' + order.id + ' con Mercado Pago'"
          @click="pay"
        >
          <span v-if="paying" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="fas fa-credit-card me-1"></i>
          {{ paying ? '…' : 'Pagar' }}
        </button>
        <button
          v-if="canContact"
          type="button"
          class="btn btn-outline-light btn-sm actions-btn-compact"
          title="Contactar al equipo"
          @click="openContactModal"
        >
          <i class="fas fa-envelope me-1"></i>Contactar
        </button>
      </div>
    </template>

    <template v-else>
      <h6 class="actions-title mb-2">
        <i class="fas fa-hand-holding-usd me-2"></i>Pago pendiente
      </h6>
      <p class="text-muted small mb-3">
        <template v-if="order.payment_method === 'online'">
          Completá el pago con Mercado Pago o escribinos si necesitás ayuda.
        </template>
        <template v-else>
          Podés pagar online con Mercado Pago o contactarnos para coordinar el pago.
        </template>
      </p>
      <div class="actions-buttons">
        <button
          v-if="canPay"
          type="button"
          class="btn btn-primary actions-btn"
          :disabled="paying"
          @click="pay"
        >
          <span v-if="paying" class="spinner-border spinner-border-sm me-2"></span>
          <i v-else class="fas fa-credit-card me-2"></i>
          {{ paying ? 'Redirigiendo…' : 'Pagar con Mercado Pago' }}
        </button>
        <button
          v-if="canContact"
          type="button"
          class="btn btn-outline-light actions-btn"
          @click="openContactModal"
        >
          <i class="fas fa-envelope me-2"></i>Contactar / coordinar pago
        </button>
      </div>
      <p v-if="!canPay && canContact" class="text-muted small mt-2 mb-0">
        El pago online no está disponible en este momento. Usá el botón de contacto.
      </p>
    </template>
  </div>

  <div
    class="modal fade order-contact-modal"
    :id="modalId"
    tabindex="-1"
    aria-hidden="true"
    ref="contactModalEl"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content order-contact-modal__content">
        <div class="modal-header border-secondary">
          <h5 class="modal-title">Contactar sobre pedido #{{ order.id }}</h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
        </div>
        <form @submit.prevent="submitContact">
          <div class="modal-body">
            <p class="text-muted small">
              Tu mensaje llegará al equipo de Kame Seeds. Te responderemos a
              <strong>{{ order.customer_email }}</strong>.
            </p>
            <label class="form-label">Mensaje *</label>
            <textarea
              v-model="contactMessage"
              class="form-control bg-dark border-secondary text-white"
              rows="4"
              required
              minlength="10"
              maxlength="2000"
              placeholder="Ej: Quiero coordinar transferencia, necesito factura A, consulta por envío…"
            />
            <p v-if="contactError" class="text-danger small mt-2 mb-0">{{ contactError }}</p>
          </div>
          <div class="modal-footer border-secondary">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
            <button type="submit" class="btn btn-primary" :disabled="contactSending">
              <span v-if="contactSending" class="spinner-border spinner-border-sm me-2"></span>
              Enviar mensaje
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import { createPayment } from '@/services/payment'
import { contactOrderAdmin } from '@/services/orders'
import { canPayOrderWithMp, canContactAdminAboutOrder } from '@/utils/orderDisplay'

const props = defineProps({
  order: { type: Object, required: true },
  mercadoPagoEnabled: { type: Boolean, default: false },
  /** 'compact' en listado; 'default' en detalle del pedido */
  layout: { type: String, default: 'default' },
})

const emit = defineEmits(['contact-sent', 'payment-started'])

const toast = useToast()
const modalId = `orderContactModal-${props.order.id}`
const contactMessage = ref('')
const contactError = ref('')
const contactSending = ref(false)
const paying = ref(false)
let contactModal = null

const canPay = computed(() => canPayOrderWithMp(props.order, props.mercadoPagoEnabled))
const canContact = computed(() => canContactAdminAboutOrder(props.order))
const showActions = computed(() => canPay.value || canContact.value)

const openContactModal = () => {
  contactError.value = ''
  contactMessage.value = ''
  if (!contactModal && window.bootstrap) {
    const el = document.getElementById(modalId)
    if (el) contactModal = new window.bootstrap.Modal(el)
  }
  contactModal?.show()
}

const pay = async () => {
  paying.value = true
  try {
    const payment = await createPayment(props.order.id)
    if (payment?.init_point) {
      emit('payment-started')
      toast.success('Redirigiendo a Mercado Pago…')
      setTimeout(() => {
        window.location.href = payment.init_point
      }, 600)
      return
    }
    toast.error('No se recibió el enlace de pago')
  } catch (e) {
    toast.error(e.response?.data?.error || e.message || 'Error al iniciar el pago')
  } finally {
    paying.value = false
  }
}

const submitContact = async () => {
  const msg = contactMessage.value.trim()
  if (msg.length < 10) {
    contactError.value = 'El mensaje debe tener al menos 10 caracteres.'
    return
  }
  contactSending.value = true
  contactError.value = ''
  try {
    const res = await contactOrderAdmin(props.order.id, msg)
    toast.success(res.detail || 'Mensaje enviado')
    contactModal?.hide()
    emit('contact-sent')
  } catch (e) {
    contactError.value =
      e.response?.data?.detail || e.message || 'No se pudo enviar el mensaje'
  } finally {
    contactSending.value = false
  }
}

onMounted(() => {
  const el = document.getElementById(modalId)
  if (el && window.bootstrap) {
    contactModal = new window.bootstrap.Modal(el)
  }
})
</script>

<style scoped>
.order-customer-actions {
  width: 100%;
  box-sizing: border-box;
  background: rgba(255, 236, 0, 0.06);
  border: 1px solid rgba(255, 236, 0, 0.25);
  border-radius: 12px;
}

.actions-title {
  color: var(--primary-color, #ffec00);
  font-weight: 700;
}

.actions-buttons {
  display: flex;
  gap: 0.6rem;
  width: 100%;
}

.actions-btn {
  width: 100%;
  max-width: 300px;
  min-height: 2.85rem;
  padding: 0.75rem 1.15rem;
  font-size: 0.95rem;
  font-weight: 600;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.actions-btn.btn-outline-light {
  border-color: rgba(255, 255, 255, 0.25);
  color: rgba(255, 255, 255, 0.9);
}

.actions-btn.btn-outline-light:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.4);
  color: #fff;
}

.order-customer-actions--compact {
  width: auto;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 0;
}

.actions-inline {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  align-items: center;
}

.actions-btn-compact {
  white-space: nowrap;
}

</style>

<!-- Sin scoped: el modal de Bootstrap puede quedar fuera de .panel-route y heredar Adumu del body -->
<style>
.order-contact-modal,
.order-contact-modal .modal-dialog,
.order-contact-modal .modal-content,
.order-contact-modal .modal-header,
.order-contact-modal .modal-body,
.order-contact-modal .modal-footer,
.order-contact-modal .modal-title,
.order-contact-modal .form-label,
.order-contact-modal .form-control,
.order-contact-modal .btn,
.order-contact-modal p,
.order-contact-modal strong {
  font-family: 'Poppins', sans-serif;
  letter-spacing: normal;
}

.order-contact-modal i[class*='fa-'],
.order-contact-modal .fa,
.order-contact-modal .fas,
.order-contact-modal .far,
.order-contact-modal .fab {
  font-family: 'Font Awesome 6 Free' !important;
  font-style: normal;
  letter-spacing: normal;
}

.order-contact-modal .fab,
.order-contact-modal .fa-brands {
  font-family: 'Font Awesome 6 Brands' !important;
}

.order-contact-modal__content {
  background: var(--grey-color, #2a2a2a) !important;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 14px;
}

.order-contact-modal .form-control {
  font-size: 0.9rem;
  line-height: 1.5;
}

.order-contact-modal .modal-title {
  font-weight: 600;
  font-size: 1.05rem;
}
</style>
